import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import React, { useEffect, useRef, useState } from "react";
import { Search } from "../../shared/Search";
import { EmptyList, TFormState, TListState } from "../../shared/modal";
import { EmptyVendorCpRecord, TVendorCp, TVendorCpRecord } from "./modal";
import { Fetch, ILazyParams } from "../../utilities/fetch";
import useToggle from "../../hooks/useToggle";
import { Form } from "./Form";

type TL = TListState<TVendorCp>;
type TF = TFormState<TVendorCpRecord>;

export const Table = () => {
    const [table, setTable] = useState(EmptyList as TL);
    const [globalSearch, setGlobalSearch] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });

    const [form, setForm] = useState({ open: false, data: EmptyVendorCpRecord } as TF);
    const hideForm = () => setForm({ open: false, data: EmptyVendorCpRecord });
    const [r, reload] = useToggle();

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTable(prev => ({ ...prev, loading: true }));
        const result = await Fetch<TL>("vendorContactPersons",
            {
                query: new URLSearchParams({
                    lazyParams: JSON.stringify(lazyParams),
                    ...(globalSearch ? { search: globalSearch } : {})
                }).toString()
            });
        if (result) {
            setTable({ data: result.data, count: result.count, loading: false });
        }
        setTable(prev => ({ ...prev, loading: false }));
    }


    useEffect(() => {
        loadTableData();
    }, [globalSearch, lazyParams, r]);

    const openNew = () => {
        setForm({ open: true, data: EmptyVendorCpRecord });
    };

    const openEdit = (r: TVendorCp) => {
        const data: TVendorCpRecord = {
            ...r,
            vendorCompany: { id: r.vendorCompanyId, name: r.vendorCompanyName }
        };
        setForm({ open: true, data: data });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    <Button label="New" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
                </div>
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Export" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />
            </React.Fragment>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">Manage Client Contact Persons</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <Search setSearch={setGlobalSearch} />
            </span>
        </div>
    );

    const actionBodyTemplate = (rowData: TVendorCp) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-button-text mr-1" onClick={() => openEdit(rowData)} />
            </div>
        );
    };

    return (
        <>
            <Toolbar className="mb-1 py-0" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small"
                value={table.data} lazy dataKey="id"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={table.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                emptyMessage="No ClientCp found."
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={table.loading}
            >
                <Column field="name" header="Vendor Contact Person" sortable />
                <Column field="email" header="Email" sortable />
                <Column field="phone No" header="Phone No" />
                <Column field="vendorCompanyName" header="Vendor Company" />
                <Column header="Action" body={actionBodyTemplate} />
            </DataTable>
            <Form open={form.open} data={form.data} hideDialog={hideForm} reload={reload} />
        </>
    );
}
